import { UserSummary } from '@beeboard/common';
import orderBy from 'lodash/orderBy';
import { defineStore } from 'pinia';
import { getItemMode, ItemMode } from '@/common';
import { formatPropertyName } from '@/composables/useSearch';
import {
  MapActivity,
  MapMilestone,
  MapPhase,
  MapWorkstream,
  useIoStore,
} from '@/store/ioStore';
import { useProjectStore } from '@/store/projectStore';
import { daysBetweenDates } from '@/utils/helpers/datetime';
import { effectiveResponsibleUserOf } from '@/utils/helpers/user-helpers';
import { useActivityProjectStore } from '../activityProjectStore';
import { useActivityTemplateStore } from '../activityTemplateStore';
import { useProfileStore } from '../profileStore';
import { useTemplateStore } from '../templateStore';
import { useUiStore } from '../uiStore';
import { calculateIdealMaxLanesPerWorkstreamInProject } from './utils/calculateIdealMaxLanesPerWorkstreamInProject';
import { calculateIdealMaxLanesPerWorkstreamInTemplate } from './utils/calculateIdealMaxLanesPerWorkstreamInTemplate';

export interface SearchMatch {
  id: string;
  isMirroredActivity: boolean;
  searchType?: 'like' | 'ai';
  relevanceScore?: number;
  matches: Array<{
    property: string;
    text: string;
    objectTitle?: string;
  }>;
  title: string;
  category:
    | 'Activity'
    | 'Completion Criteria'
    | 'Action'
    | 'Tag'
    | 'Topic'
    | 'Link'
    | 'Document';
  objectTitles: string[];
}

export type MapContext =
  | ''
  | 'milestone'
  | 'workstream'
  | 'activity'
  | 'phase'
  | 'link'
  | 'mirroredActivity'
  | 'mirroredMilestone';

export type ToolMode =
  | 'normal'
  | 'link'
  | 'addingActivity'
  | 'addingMilestone'
  | 'addingPhase'
  | 'addingWorkstream'
  | 'movingItem'
  | 'spacePhase'
  | 'spaceFull'
  | 'splitPhase'
  | 'compress'
  | 'movingItemVertically'
  | 'markup';

type LinkMode = 'none' | 'single' | 'multi' | 'all';
type LinkMilestoneMode = 'none' | 'required' | 'all';
type LabelMode = 'none' | 'title' | 'auto';
type TimeLineTemplateMode = 'none' | 'positions';
// Occlusion-group markers on the canvas map (WI #5531): collapse stacked /
// label-starved activities into counted markers. Canvas renderer only.
type ActivityGroupingMode = 'on' | 'off';

export type FilterRole = 'responsible' | 'approver';
export const FILTER_ROLES: FilterRole[] = ['responsible', 'approver'];

interface MapState {
  compressToolAnchor: number;
  compressToolFixedX?: number;
  /** WI #5622 — milestones fixed for the current compress-tool activation.
   * Per activation only: cleared when the tool is left, never persisted. */
  compressFixedMilestoneIds: string[];
  /** WI #5622 — milestone whose compress fix toggle is under the pointer. */
  hoveredCompressFixMilestoneId: string;
  height: number;
  searchResults: SearchMatch[];
  searchQuery: string;
  showSearchResults: boolean;
  focusedFunctions: string[];
  // Responsible-user ids to filter by (project kanban board and, since
  // WI #5605, the map sidebar's user list). Ids only — kanban matches
  // entries against UserSummary.id.
  focusedUsers: string[];
  // WI #5605: mirrored-only responsibles to filter by — map sidebar only.
  // Mirrored activities carry just the source user's "first_name last_name"
  // display name (no UserSummary, no username), so these entries are display
  // names. Kept out of `focusedUsers` so kanban's id-based matching never
  // sees them.
  focusedMirroredNames: string[];
  focusedTags: string[];
  focusedProjects: string[];
  // WI #5630: item modes to filter by (map sidebar) — the same
  // state-plus-colour option list as the activity status dropdown
  focusedModes: ItemMode[];
  // The role a focused function / user / "Assigned to me" must hold on an
  // activity to match.
  focusedRole: FilterRole;
  // The roles "Assigned to me" matches (any of them) — independent of
  // `focusedRole`.
  meRoles: FilterRole[];
  frozenActivities: { id: string; position: number }[];
  frozenMilestones: { id: string; position: number }[];
  frozenPhases: { id: string; start: number; duration: number }[];
  hoveredActivity: string;
  // WI #5570: the activity whose HEXAGON (not its banner card) the cursor is on,
  // held across the small dead zone between the hexagon and its flipped card.
  // The banner FLIP side keys off this signal (plus selection) only, never off
  // banner-card hover, so a card near the right edge can't oscillate. It is a
  // PARALLEL signal to `hoveredActivity` on purpose — narrowing `hoveredActivity`
  // would regress the hexagon/label/link painters that also read it.
  hexagonHoveredActivity: string;
  hoveredSearchActivity: string;
  hoveredMilestone: string;
  hoveredWorkstreamId: string;
  // WI #5563: workstream whose canvas expand/collapse button is hovered, driving
  // the DOM tooltip (id + whether it currently shows the collapse glyph).
  hoveredExpandWorkstreamId: string;
  hoveredExpandIsExpanded: boolean;
  isAnimating: boolean;
  isDragging: boolean;
  isExportingPDF: boolean;
  isTemplate: boolean;
  isVisibleActivityModal: boolean;
  isVisibleLinkModal: boolean;
  isVisibleMilestoneModal: boolean;
  isVisiblePhaseModal: boolean;
  isVisibleWorkstreamModal: boolean;
  isHidingActivityColors: boolean;
  isHidingMilestoneColors: boolean;
  isTodoDragOver: boolean;
  isTodoDragBanned: boolean;
  todoDragTitle: string;
  todoDragHoveredActivityId: string;
  todoDragPayload: unknown;
  todoDragOriginalPosX: number | null;
  todoDragSource: string;
  /** Id of the source record being dragged; project mail (WI #5643) needs it to
   * link the created entity back to the mail it came from. */
  todoDragSourceItemId: string;
  isVisibleTodoDragSidebar: boolean;
  isMeFilter: boolean;
  isFavoriteFilter: boolean;
  isRecentChangesFilter: boolean;
  isHideMirroredFilter: boolean;
  // WI #5596: hide-mode — an active filter/search removes non-matching
  // activities entirely instead of dimming them. Persisted per map.
  isFilterHideMode: boolean;
  libraryFilter: 'none' | 'library' | 'custom';
  isMilestoneOverlay: boolean;
  /** A wheel zoom with a tool active took the view past the map's start or
   * end (zoomAtPointer); it glides back once the wheel rests
   * (useMapOverscrollSettle). */
  isOverscrolled: boolean;
  linkMilestoneMode: LinkMilestoneMode;
  linkMode: LinkMode;
  labelMode: LabelMode;
  activityGroupingMode: ActivityGroupingMode;
  mapId: string;
  mouseX: number;
  mouseY: number;
  movingContext: MapContext;
  movingTarget: string;
  /** Everything a horizontal activity move carries, when more than its
   * target: the multi-selection the target belongs to, or the members of a
   * stack (collapsed group marker) dragged as one. Empty for one activity. */
  movingActivityIds: string[];
  /** What a horizontal activity move leaves where it is although it was
   * part of what the user grabbed — the multi-selection or the stack — because
   * the user may not move it. Ringed red while dragging. */
  leftBehindActivityIds: string[];
  movingItemOffset: number;
  movingItemYPos: number;
  offset: number;
  readonly: boolean;
  isSearching: boolean;
  selectedContext: MapContext;
  selectedFunctions: string[];
  selectedLink: [string, string];
  selectedId: string;
  selectedTaskId: string;
  selectedTaskType: string;
  selectedIds: string[];
  rectangleSelectStart: { x: number; y: number } | null;
  rectangleSelectEnd: { x: number; y: number } | null;
  isRectangleSelecting: boolean;
  lassoSelectPoints: { x: number; y: number }[];
  isLassoSelecting: boolean;
  showLinkDeleteModal: boolean;
  shouldShowActivityBanner: Record<string, boolean>;
  spaceToolAnchor: number;
  spaceToolFixedX?: number;
  textSizes: Record<string, number[]>;
  timeLineTemplateMode: TimeLineTemplateMode;
  toolMode: ToolMode;
  uniqueEntityIdsChanged: string[];
  width: number;
  /** Width (px) of the map's right part under the context bar, 0 while it is
   * closed: the bar floats over the map without resizing it (RemapPage). */
  contextbarOverlap: number;
  workstreamLanes: Record<string, number>;
  zoom: number;
  zoomResistanceCount?: number;
}

interface AnimOpts {
  initZoom: number;
  initOffset: number;
  initTime: number;
  duration: number;
  zoom: number;
  offset: number;
}

export const START_X = 180;
export const START_Y = 200;
export const MINI_Y = 10;
export const PHASE_Y = 60;
export const MAX_ZOOM = 1000;
// Room right of the map's last day, even fully zoomed out or panned to the end:
// a group marker on that day reaches 23px past its centre (stacked hexagon +
// count badge), a selection ring around a hexagon 20px. At 10px both were cut
// off by the canvas edge.
const MASK_MARGIN_RIGHT = 24;
export const FAKE_WORKSTREAM_ID = 'hidden-workstreams';
const FAKE_WORKSTREAM_HEIGHT = 60;

const lanes = [
  [0],
  [0, 1],
  [1, 2, 0],
  [1, 2, 0, 3],
  [2, 3, 1, 4, 0],
  [2, 4, 0, 3, 1, 5],
  [3, 1, 5, 0, 2, 4, 6],
  [4, 2, 6, 1, 3, 5, 7, 0],
  [5, 3, 7, 2, 4, 6, 0, 1, 8],
  [6, 4, 8, 3, 5, 7, 1, 2, 9, 0],
  [6, 4, 8, 3, 5, 7, 1, 2, 9, 0, 10],
];

export const useMapStore = defineStore('remapStore', {
  state: (): MapState => ({
    compressToolAnchor: 0,
    compressToolFixedX: undefined,
    compressFixedMilestoneIds: [],
    hoveredCompressFixMilestoneId: '',
    searchResults: [],
    searchQuery: '',
    showSearchResults: false,
    focusedFunctions: [],
    focusedUsers: [],
    focusedMirroredNames: [],
    focusedTags: [],
    focusedProjects: [],
    focusedModes: [],
    focusedRole: 'responsible',
    meRoles: ['responsible'],
    frozenActivities: [],
    frozenMilestones: [],
    frozenPhases: [],
    height: 200,
    hoveredActivity: '',
    hexagonHoveredActivity: '',
    hoveredSearchActivity: '',
    hoveredMilestone: '',
    hoveredWorkstreamId: '',
    hoveredExpandWorkstreamId: '',
    hoveredExpandIsExpanded: false,
    isAnimating: false,
    isDragging: false,
    isExportingPDF: false,
    isHidingActivityColors: false,
    isHidingMilestoneColors: false,
    isTodoDragOver: false,
    isTodoDragBanned: false,
    todoDragTitle: '',
    todoDragHoveredActivityId: '',
    todoDragPayload: null,
    todoDragOriginalPosX: null,
    todoDragSource: '',
    todoDragSourceItemId: '',
    isVisibleTodoDragSidebar: false,
    isTemplate: false,
    isVisibleActivityModal: false,
    isVisibleLinkModal: false,
    isVisibleMilestoneModal: false,
    isVisiblePhaseModal: false,
    isVisibleWorkstreamModal: false,
    isFavoriteFilter: false,
    isMeFilter: false,
    isRecentChangesFilter: false,
    isHideMirroredFilter: false,
    isFilterHideMode: false,
    libraryFilter: 'none' as 'none' | 'library' | 'custom',
    isMilestoneOverlay: false,
    isOverscrolled: false,
    linkMilestoneMode: 'all',
    linkMode: 'single',
    labelMode: 'auto',
    activityGroupingMode: 'on',
    mapId: '',
    mouseX: 0,
    mouseY: 0,
    movingContext: '',
    movingTarget: '',
    movingActivityIds: [],
    leftBehindActivityIds: [],
    movingItemOffset: 0,
    movingItemYPos: 0,
    offset: 0,
    readonly: false,
    isSearching: false,
    selectedContext: '',
    selectedFunctions: [],
    selectedLink: ['', ''],
    selectedId: '',
    selectedTaskId: '',
    selectedTaskType: '',
    selectedIds: [],
    rectangleSelectStart: null,
    rectangleSelectEnd: null,
    isRectangleSelecting: false,
    lassoSelectPoints: [],
    isLassoSelecting: false,
    showLinkDeleteModal: false,
    shouldShowActivityBanner: {},
    spaceToolAnchor: 0,
    spaceToolFixedX: undefined,
    textSizes: {},
    timeLineTemplateMode: 'none',
    toolMode: 'normal',
    uniqueEntityIdsChanged: [],
    width: 1000,
    contextbarOverlap: 0,
    workstreamLanes: {},
    zoom: 10,
  }),

  getters: {
    mapLength() {
      return useIoStore().mapLength;
    },

    virtualMapLength() {
      return useIoStore().virtualMapLength;
    },

    isLinking(state) {
      return state.toolMode === 'link';
    },

    isAddingActivity(state) {
      return state.toolMode === 'addingActivity';
    },

    isAddingMilestone(state) {
      return state.toolMode === 'addingMilestone';
    },

    isAddingPhase(state) {
      return state.toolMode === 'addingPhase';
    },

    isAddingWorkstream(state) {
      return state.toolMode === 'addingWorkstream';
    },

    isMovingItem(state) {
      return state.toolMode === 'movingItem';
    },

    isMovingItemVertically(state) {
      return state.toolMode === 'movingItemVertically';
    },

    /**
     * The activities an activity move drags: what it carries
     * (`movingActivityIds`), else the target alone — never the rest of a
     * selection the move leaves behind.
     */
    movedActivityIds(state): string[] {
      return state.movingActivityIds.length
        ? state.movingActivityIds
        : [state.movingTarget];
    },

    isSpacing(state) {
      return state.toolMode === 'spacePhase' || state.toolMode === 'spaceFull';
    },

    isSplittingPhase(state) {
      return state.toolMode === 'splitPhase';
    },

    isCompressingPhase(state) {
      return state.toolMode === 'compress';
    },

    /** WI #5622 — a milestone fixed for the current compress-tool activation. */
    isCompressFixedMilestone(state) {
      return (milestoneId: string): boolean =>
        state.compressFixedMilestoneIds.includes(milestoneId);
    },

    /**
     * WI #5622 — a milestone that may be fixed for a compress: an intermediate
     * (not final) milestone of this project that is not done (done items are
     * already hard walls for the tool), not mirrored and not hidden.
     */
    isCompressFixableMilestone() {
      return (milestone: MapMilestone): boolean =>
        !milestone.isFinal &&
        milestone.state !== 'done' &&
        !milestone.mirrored &&
        !milestone.isHiddenOnMap;
    },

    /**
     * WI #5622 — the fixed milestones that belong to the phase being
     * compressed. Only these bound the drag and ride along in the request.
     */
    compressFixedMilestonesInPhase(): MapMilestone[] {
      if (!this.compressingPhase || !this.compressFixedMilestoneIds.length) {
        return [];
      }
      const ioStore = useIoStore();

      return ioStore
        .intermediateMilestonesInPhase(this.compressingPhase.id)
        .filter((milestone) => this.isCompressFixedMilestone(milestone.id));
    },

    /**
     * WI #5622 — the fixed milestone the current compress drag pushes
     * against: the position of the nearest fixed milestone of the compressing
     * phase in the drag direction (`posX` vs. anchor), or undefined when the
     * phase boundary bounds the drag. Drives the live preview so it matches
     * the backend rescale (compressInPhase.ts) exactly.
     */
    compressWallPosition(): number | undefined {
      const anchor = this.compressToolAnchor;
      const positions = this.compressFixedMilestonesInPhase.map(
        (milestone) => milestone.originalPosition,
      );
      if (this.posX > anchor) {
        const walls = positions.filter((position) => position >= anchor);

        return walls.length ? Math.min(...walls) : undefined;
      }
      if (this.posX < anchor) {
        const walls = positions.filter((position) => position < anchor);

        return walls.length ? Math.max(...walls) : undefined;
      }

      return undefined;
    },

    /**
     * WI #5622 — true for an item on or beyond the compress wall in the drag
     * direction: it stays where it is (the wall milestone included).
     */
    isBeyondCompressWall() {
      return (originalPosition: number): boolean => {
        const wall = this.compressWallPosition;
        if (wall === undefined) {
          return false;
        }

        return this.posX > this.compressToolAnchor
          ? originalPosition >= wall
          : originalPosition <= wall;
      };
    },

    isMarkup(state) {
      return state.toolMode === 'markup';
    },

    movingItemYMin() {
      // WI #5563/#5596: whenever the placeholder row is reserved (manually
      // hidden workstreams, temporary expand, or filter hide-mode), vertical
      // moves start below it.
      if (this.hiddenWorkstreamsHeight > 0) {
        return START_Y + FAKE_WORKSTREAM_HEIGHT + 20; // just after the fake workstream
      }

      return START_Y + 20; // just after the top of the map
    },

    movingItemYMax(state) {
      return state.height - 40; // just before the bottom of the map
    },

    // the size of the visible portion of the activities map in the screen
    // (it does not count the translucent part on the left)
    maskSize(state) {
      return {
        width: state.width - START_X - MASK_MARGIN_RIGHT,
        height: state.height - START_Y,
      };
    },

    minZoom(): number {
      return this.maskSize.width / this.mapLength;
    },

    scaleX() {
      return (x: number): number => {
        return x * this.zoom;
      };
    },

    posX(state): number {
      return Math.round(state.mouseX / state.zoom);
    },

    minOffset() {
      return () => {
        const ioStore = useIoStore();
        if (ioStore.hiddenPhases.size > 0 && !this.isExportingPDF) {
          return this.scaleX(ioStore.firstVisiblePhase.start);
        }

        return 0;
      };
    },

    maxOffset() {
      return () => {
        const ioStore = useIoStore();
        // return limit to last visible phase if there are hidden phases
        if (ioStore.hiddenPhases.size > 0) {
          return (
            this.scaleX(ioStore.lastVisiblePhase.end) - this.maskSize.width
          );
        }

        return this.scaleX(this.mapLength) - this.maskSize.width;
      };
    },

    /** How far the view reaches past the map's start (< 0) or end (> 0), px. */
    overscroll(): number {
      if (!this.isOverscrolled) {
        return 0;
      }
      const minOffset = this.minOffset();
      const maxOffset = this.maxOffset();
      if (this.offset < minOffset) {
        return this.offset - minOffset;
      }

      return this.offset > maxOffset ? this.offset - maxOffset : 0;
    },

    idealLanesInAllVisibleWorkstreams() {
      return (): number => {
        const ioStore = useIoStore();
        // WI #5596: filter-hidden workstreams free their vertical space for
        // the visible ones, exactly like manually-hidden ones.
        const visibleWorkstreams = ioStore.workstreams.filter(
          (ws) =>
            !ws.isHiddenOnMap && !this.filterHiddenWorkstreamIds.has(ws.id),
        );

        return visibleWorkstreams.reduce(
          (acc, ws) => acc + (this.idealMaxLanesPerWorkstream[ws.id] || 0) + 1,
          0,
        );
      };
    },

    // one fake workstream at the top of the map to represent all hidden workstreams
    hiddenWorkstreamsHeight(state) {
      const ioStore = useIoStore();
      // WI #5563: effective count, so a temporary expand (which hides every
      // other workstream without persisting) also reserves the placeholder row.
      // WI #5596: filter hide-mode parks hidden activities' link anchors on
      // the strip, so it reserves the row too while it is hiding anything.
      if (
        (ioStore.hiddenWorkstreamsOnMapCount > 0 ||
          this.hasFilterHiddenActivities) &&
        !this.isExportingPDF
      ) {
        // WI #5596: when NO workstream row remains visible (all manually
        // hidden and/or filter-hidden), a 60px strip over an empty map looks
        // broken — let the placeholder span the whole map area instead.
        const anyVisible = ioStore.workstreams.some(
          (ws) =>
            !ws.isHiddenOnMap && !this.filterHiddenWorkstreamIds.has(ws.id),
        );

        return anyVisible
          ? FAKE_WORKSTREAM_HEIGHT
          : Math.max(FAKE_WORKSTREAM_HEIGHT, state.height - START_Y);
      }

      return 0;
    },

    workstreamHeight(state) {
      return (wsId: string): number => {
        if (!state.mapId || !state.height) {
          return 100;
        }

        if (wsId === FAKE_WORKSTREAM_ID) {
          return this.hiddenWorkstreamsHeight;
        }

        const totalLanes = this.idealLanesInAllVisibleWorkstreams();
        const lanesInWorkstream =
          (this.idealMaxLanesPerWorkstream[wsId] || 0) + 1;

        const availableHeight =
          state.height - START_Y - this.hiddenWorkstreamsHeight;
        const ratio = lanesInWorkstream / totalLanes;

        return availableHeight * ratio;
      };
    },

    /** Y position of the workstream*/
    workstreamY() {
      return (wsId: string) => {
        const ioStore = useIoStore();

        if (wsId === FAKE_WORKSTREAM_ID) {
          return START_Y;
        }

        const workstream = ioStore.workstreamById(wsId);
        // Filter visible workstreams (WI #5596: filter-hidden rows are out of
        // the layout too, like manually-hidden ones)
        const visibleWorkstreams = ioStore.workstreams.filter(
          (ws) =>
            !ws.isHiddenOnMap && !this.filterHiddenWorkstreamIds.has(ws.id),
        );

        // summarize the heights of the workstreams
        // with a position lower than the current workstream
        const workstreamY = visibleWorkstreams
          .filter((ws) => ws.position < workstream.position)
          .reduce(
            (acc, ws) => acc + this.workstreamHeight(ws.id),
            START_Y + this.hiddenWorkstreamsHeight, // start after the hidden ws, if any
          );

        return workstreamY;
      };
    },

    /**
     * Calculates the ideal number of Activity Lanes per Workstream
     * based on the Activities distribution throughout the project.
     *
     * @returns A dictionary of the ideal max number of Activity Lanes per Workstream
     */
    idealMaxLanesPerWorkstream(): Record<string, number> {
      const ioStore = useIoStore();
      const workstreams = ioStore.workstreams;
      const activitiesInWorkstream = ioStore.activitiesInWorkstream;
      const projectDuration = ioStore.projectDuration;

      if (this.isTemplate) {
        return calculateIdealMaxLanesPerWorkstreamInTemplate(
          workstreams,
          activitiesInWorkstream,
          projectDuration,
          lanes.length,
        );
      }

      return calculateIdealMaxLanesPerWorkstreamInProject(
        workstreams,
        activitiesInWorkstream,
        ioStore.projectStart,
        projectDuration,
        lanes.length,
      );
    },

    workstreamLineLength(state): number {
      return state.isExportingPDF
        ? this.scaleX(useIoStore().lastVisiblePhase.end)
        : 4000;
    },

    firstVisiblePos(state) {
      if (state.isExportingPDF) {
        return 0;
      }
      const n = Math.floor((state.offset - START_X) / state.zoom);

      return n < 0 ? 0 : n;
    },

    lastVisiblePos(state): number {
      if (state.isExportingPDF) {
        return this.scaleX(useIoStore().lastVisiblePhase.end);
      }

      return Math.ceil((state.offset - START_X + state.width) / state.zoom);
    },

    spacingPhase(state): MapPhase | undefined {
      const ioStore = useIoStore();
      if (!state.spaceToolAnchor) {
        return;
      }

      return ioStore.phaseByPosition(this.spaceToolAnchor);
    },

    compressingPhase(state): MapPhase | undefined {
      const ioStore = useIoStore();
      if (!state.compressToolAnchor) {
        return;
      }

      return ioStore.phaseByPosition(this.compressToolAnchor);
    },

    spacingActivities(): MapActivity[] {
      const ioStore = useIoStore();
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return [];
      }

      return ioStore
        .activitiesInPhase(spacingPhase.id)
        .filter((a) => a.originalPosition >= this.spaceToolAnchor)
        .sort((a, b) => (a.originalPosition > b.originalPosition ? -1 : 1));
    },

    spacingMilestones(): MapMilestone[] {
      const ioStore = useIoStore();
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return [];
      }

      return ioStore.milestones.filter(
        (ms) =>
          ms.phase === spacingPhase.id &&
          ms.originalPosition >= this.spaceToolAnchor,
      );
    },

    spacingIntermediateMilestones(): MapMilestone[] {
      return this.spacingMilestones.filter(({ isFinal }) => !isFinal);
    },

    lastNonSpacingActivityPosition(): number {
      const ioStore = useIoStore();
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return 0;
      }
      const nonSpacingActivities = ioStore
        .activitiesInPhase(spacingPhase.id)
        .filter((a) => !this.spacingActivities.includes(a));
      const lastNonSpacingActivity = nonSpacingActivities.sort((a, b) =>
        a.originalPosition > b.originalPosition ? -1 : 1,
      )[0];
      const lastPos = lastNonSpacingActivity
        ? lastNonSpacingActivity.originalPosition
        : spacingPhase.start;

      return lastPos + 1;
    },

    lastNonSpacingMilestonePosition(): number {
      const ioStore = useIoStore();
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return 0;
      }
      const spacingMilestones = this.spacingMilestones;
      const nonSpacingMilestones = ioStore
        .intermediateMilestonesInPhase(spacingPhase.id)
        .filter((a) => !spacingMilestones.includes(a));
      const lastNonSpacingMilestone = nonSpacingMilestones.sort((a, b) =>
        a.originalPosition > b.originalPosition ? -1 : 1,
      )[0];

      return lastNonSpacingMilestone
        ? lastNonSpacingMilestone.originalPosition
        : spacingPhase.start;
    },

    lastNonSpacingPosition(): number {
      const activity = this.lastNonSpacingActivityPosition;
      const milestone = this.lastNonSpacingMilestonePosition;

      return activity > milestone ? activity : milestone;
    },

    lastNonCompressingPosition(): number {
      if (!this.compressingPhase) {
        return 0;
      }
      const ioStore = useIoStore();
      const anchor = this.compressToolAnchor;
      // Get all activities and milestones for the current compressing phase.
      const activities = ioStore.activitiesInPhase(this.compressingPhase.id);
      const milestones = ioStore.intermediateMilestonesInPhase(
        this.compressingPhase.id,
      );
      // A done item on the right of (or at) the anchor blocks the drag entirely
      // (every item on that side would move); otherwise the phase end bounds it.
      const isDoneOnRight = [...activities, ...milestones].some(
        (item) => item.state === 'done' && item.originalPosition >= anchor,
      );
      const doneLimit = isDoneOnRight ? anchor : this.compressingPhase.end - 1;

      // WI #5622 — the nearest fixed milestone on the right is a wall: the
      // compress must fit before it, so the drag stops one column short of it
      // (mirrors the phase-end limit above; the backend rejects a compress that
      // reaches the milestone). A fixed milestone on the anchor blocks the drag.
      const fixedWalls = this.compressFixedMilestonesInPhase
        .filter((milestone) => milestone.originalPosition >= anchor)
        .map((milestone) => milestone.originalPosition);
      const fixedLimit = fixedWalls.length
        ? Math.max(anchor, Math.min(...fixedWalls) - 1)
        : doneLimit;

      return Math.min(doneLimit, fixedLimit);
    },

    firstNonCompressingPosition(): number {
      if (!this.compressingPhase) {
        return 0;
      }

      const ioStore = useIoStore();
      const anchor = this.compressToolAnchor;
      // Get all activities and milestones for the current compressing phase
      const activities = ioStore.activitiesInPhase(this.compressingPhase.id);
      const milestones = ioStore.intermediateMilestonesInPhase(
        this.compressingPhase.id,
      );
      // A done item left of the anchor blocks the drag entirely (every item on
      // that side would move); otherwise the phase start bounds it.
      const isDoneOnLeft = [...activities, ...milestones].some(
        (item) => item.state === 'done' && item.originalPosition < anchor,
      );
      const doneLimit = isDoneOnLeft ? anchor : this.compressingPhase.start + 2;

      // WI #5622 — the nearest fixed milestone on the left is a wall: the drag
      // stops one column after it (mirrors the phase-start limit above).
      const fixedWalls = this.compressFixedMilestonesInPhase
        .filter((milestone) => milestone.originalPosition < anchor)
        .map((milestone) => milestone.originalPosition);
      const fixedLimit = fixedWalls.length
        ? Math.max(...fixedWalls) + 1
        : doneLimit;

      return Math.max(doneLimit, fixedLimit);
    },

    spaceMinOffset(): number {
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return 0;
      }

      const spacingActivities = this.spacingActivities.slice();
      const firstSpacingActivity = spacingActivities.sort((a, b) =>
        a.originalPosition > b.originalPosition ? 1 : -1,
      )[0];

      const spacingMilestones = this.spacingMilestones.slice();
      const firstSpacingMilestone = spacingMilestones.sort((a, b) =>
        a.originalPosition > b.originalPosition ? 1 : -1,
      )[0];

      const lastPos = this.lastNonSpacingPosition;
      let firstSpacingPosition = spacingPhase.end;

      if (firstSpacingActivity) {
        firstSpacingPosition = firstSpacingActivity.originalPosition;
      }

      if (
        firstSpacingMilestone &&
        firstSpacingMilestone.originalPosition < firstSpacingPosition
      ) {
        firstSpacingPosition = firstSpacingMilestone.originalPosition;
      }

      return -lastPos + firstSpacingPosition;
    },

    spaceMaxOffset(): number {
      const spacingPhase = this.spacingPhase;
      if (!spacingPhase) {
        return 0;
      }

      const spacingActivities = this.spacingActivities.slice();
      const lastSpacingActivity = spacingActivities.sort((a, b) =>
        a.originalPosition > b.originalPosition ? -1 : 1,
      )[0];

      const spacingMilestones = this.spacingIntermediateMilestones.slice();
      const lastSpacingMilestone = spacingMilestones.sort((a, b) =>
        a.originalPosition > b.originalPosition ? -1 : 1,
      )[0];

      if (!lastSpacingActivity && !lastSpacingMilestone) {
        return 0;
      }
      if (!lastSpacingActivity) {
        return spacingPhase.end - lastSpacingMilestone.originalPosition;
      }
      if (!lastSpacingMilestone) {
        return spacingPhase.end - lastSpacingActivity.originalPosition;
      }
      const lastItem =
        lastSpacingActivity.originalPosition >
        lastSpacingMilestone.originalPosition
          ? lastSpacingActivity
          : lastSpacingMilestone;

      return spacingPhase.end - lastItem.originalPosition;
    },

    spaceToolX(): number {
      if (!this.isSpacing) {
        return 0;
      }
      const posX = this.posX;
      const anchor = this.spaceToolAnchor;
      if (anchor > posX) {
        const min = anchor - this.spaceMinOffset;

        return posX > min ? posX : min;
      }
      if (this.toolMode === 'spacePhase') {
        const max = anchor + this.spaceMaxOffset;

        return posX > max ? max : posX;
      } else {
        return posX;
      }
    },

    compressToolX(): number {
      if (!this.isCompressingPhase) {
        return 0;
      }
      const posX = this.posX;

      const max = this.lastNonCompressingPosition;
      const min = this.firstNonCompressingPosition;

      if (posX > max) {
        return max;
      } else if (posX < min) {
        return min;
      } else {
        return posX;
      }
    },

    isSpacingActivity() {
      return (activity: MapActivity) => {
        if (!this.isSpacing) {
          return false;
        }
        const phase = this.spacingPhase;

        return (
          phase &&
          activity.originalPosition >= this.spaceToolAnchor &&
          (this.toolMode === 'spacePhase' ? activity.phase === phase.id : true)
        );
      };
    },

    isCompressingActivity() {
      return (activity: MapActivity) => {
        if (!this.isCompressingPhase) {
          return false;
        }
        const phase = this.compressingPhase;
        if (this.posX > this.compressToolAnchor) {
          return (
            phase &&
            activity.originalPosition >= this.compressToolAnchor &&
            (this.toolMode === 'compress'
              ? activity.phase === phase.id
              : true) &&
            !this.isBeyondCompressWall(activity.originalPosition)
          );
        }
        if (this.posX < this.compressToolAnchor) {
          return (
            phase &&
            activity.originalPosition < this.compressToolAnchor &&
            (this.toolMode === 'compress'
              ? activity.phase === phase.id
              : true) &&
            !this.isBeyondCompressWall(activity.originalPosition)
          );
        }
      };
    },

    isSpacingMilestone() {
      return (milestone: MapMilestone) => {
        if (!this.isSpacing) {
          return false;
        }
        const phase = this.spacingPhase;

        return (
          phase &&
          milestone.originalPosition >= this.spaceToolAnchor &&
          (this.toolMode !== 'spacePhase'
            ? true
            : milestone.phase === phase.id && !milestone.isFinal)
        );
      };
    },

    isCompressingMilestone() {
      return (milestone: MapMilestone) => {
        if (!this.isCompressingPhase) {
          return false;
        }
        const phase = this.compressingPhase;
        if (this.posX > this.compressToolAnchor) {
          return (
            phase &&
            milestone.originalPosition >= this.compressToolAnchor &&
            (this.toolMode === 'compress'
              ? milestone.phase === phase.id
              : true) &&
            !this.isBeyondCompressWall(milestone.originalPosition)
          );
        }
        if (this.posX < this.compressToolAnchor) {
          return (
            phase &&
            milestone.originalPosition < this.compressToolAnchor &&
            (this.toolMode === 'compress'
              ? milestone.phase === phase.id
              : true) &&
            !this.isBeyondCompressWall(milestone.originalPosition)
          );
        }
      };
    },

    isVisible() {
      return (x: number) => {
        const realX = this.scaleX(x) - this.offset + START_X;

        return realX > -50 && realX < this.width + 50;
      };
    },

    phaseDuration(state) {
      const ioStore = useIoStore();

      return (phase: MapPhase): number => {
        if (!state.mapId) {
          return 0;
        }

        if (this.frozenPhases.length) {
          const duration = this.frozenPhases.find(
            ({ id }) => id === phase.id,
          )?.duration;
          if (typeof duration === 'undefined') {
            throw new Error('missing phase');
          }

          return duration;
        }

        if (this.toolMode === 'spaceFull') {
          if (this.spacingPhase?.idx !== phase.idx) {
            return phase.duration;
          }

          return phase.duration + this.spaceToolX - this.spaceToolAnchor;
        }

        if (
          this.toolMode === 'movingItem' &&
          this.movingContext === 'milestone'
        ) {
          const movingMilestone = ioStore.milestone(this.movingTarget);
          if (!movingMilestone.isFinal) {
            return phase.duration;
          }
          if (movingMilestone.phase === phase.id) {
            return phase.duration - this.movingItemOffset;
          }
          const movingPhase = ioStore.phase(movingMilestone.phase);
          if (movingPhase.idx === phase.idx - 1) {
            return phase.duration + this.movingItemOffset;
          }
        }

        return phase.duration;
      };
    },

    phaseStart(state) {
      const ioStore = useIoStore();

      return (phase: MapPhase): number => {
        if (!state.mapId) {
          return 0;
        }
        if (phase.idx === 0) {
          return 0;
        }

        if (this.frozenPhases.length) {
          const pos = this.frozenPhases.find(
            ({ id }) => id === phase.id,
          )?.start;
          if (typeof pos === 'undefined') {
            throw new Error('missing phase');
          }

          return pos;
        }

        if (this.toolMode === 'spaceFull') {
          if (!this.spaceToolAnchor) {
            return phase.start;
          }
          if ((this.spacingPhase?.idx || 0) >= phase.idx) {
            return phase.start;
          }

          return phase.start + this.spaceToolX - this.spaceToolAnchor;
        }

        if (
          this.toolMode === 'movingItem' &&
          this.movingContext === 'milestone'
        ) {
          const movingMilestone = ioStore.milestone(this.movingTarget);
          if (!movingMilestone.isFinal) {
            return phase.start;
          }
          const movingPhase = ioStore.phase(movingMilestone.phase);
          if (movingPhase.idx === phase.idx - 1) {
            return phase.start - this.movingItemOffset;
          }
        }

        return phase.start;
      };
    },

    milestonePos(state) {
      return (milestone: MapMilestone): number => {
        if (!state.mapId) {
          return 0;
        }
        if (
          this.movingContext === 'milestone' &&
          this.movingTarget === milestone.id
        ) {
          return milestone.originalPosition - this.movingItemOffset;
        }
        if (this.frozenMilestones.length) {
          const pos = this.frozenMilestones.find(
            ({ id }) => id === milestone.id,
          )?.position;
          if (!pos) {
            throw new Error('missing activity');
          }

          return pos;
        }
        if (this.isSpacingMilestone(milestone)) {
          const x = this.spaceToolFixedX || this.spaceToolX;

          return milestone.originalPosition - this.spaceToolAnchor + x;
        }
        if (this.isCompressingMilestone(milestone) && this.compressingPhase) {
          const phase = useIoStore().phases.find(
            (p) => p.id === this.compressingPhase?.id,
          );
          if (!phase) {
            return milestone.originalPosition;
          }
          const x = this.compressToolFixedX || this.compressToolX;
          const distanceFromStartDay =
            milestone.originalPosition - this.compressToolAnchor;
          const lengthToCompress = Math.abs(x - this.compressToolAnchor);
          let newPosition: number;
          let remainingLengthOfPhase: number;
          // WI #5622 — a fixed milestone in the drag direction bounds the
          // preview exactly like the backend rescale (compressInPhase.ts).
          const wall = this.compressWallPosition;
          if (this.posX > this.compressToolAnchor) {
            remainingLengthOfPhase =
              (wall ?? phase.end) - this.compressToolAnchor;
            newPosition = Math.round(
              (distanceFromStartDay / remainingLengthOfPhase) *
                (remainingLengthOfPhase - lengthToCompress) +
                lengthToCompress +
                this.compressToolAnchor,
            );
          } else if (this.posX < this.compressToolAnchor) {
            remainingLengthOfPhase =
              this.compressToolAnchor - (wall ?? phase.start + 1);
            newPosition = Math.round(
              (distanceFromStartDay / remainingLengthOfPhase) *
                (remainingLengthOfPhase - lengthToCompress) -
                lengthToCompress +
                this.compressToolAnchor,
            );
          } else {
            newPosition = milestone.originalPosition;
          }

          return Math.round(newPosition);
        }

        return milestone.originalPosition;
      };
    },

    activityPos(state) {
      return (activity: MapActivity): number => {
        if (!state.mapId) {
          return 0;
        }
        if (
          this.isMovingItem &&
          this.movingContext === 'activity' &&
          this.movedActivityIds.includes(activity.id)
        ) {
          return activity.originalPosition - this.movingItemOffset;
        }
        if (this.frozenActivities.length) {
          const pos = this.frozenActivities.find(
            ({ id }) => id === activity.id,
          )?.position;
          if (!pos && pos !== 0) {
            throw new Error('missing activity');
          }

          return pos;
        }
        if (this.isSpacingActivity(activity)) {
          const x = this.spaceToolFixedX || this.spaceToolX;

          return activity.originalPosition - this.spaceToolAnchor + x;
        }
        if (this.isCompressingActivity(activity) && this.compressingPhase) {
          const phase = useIoStore().phases.find(
            (p) => p.id === this.compressingPhase?.id,
          );
          if (!phase) {
            return activity.originalPosition;
          }
          const x = this.compressToolFixedX || this.compressToolX;
          const distanceFromStartDay =
            activity.originalPosition - this.compressToolAnchor;
          const lengthToCompress = Math.abs(x - this.compressToolAnchor);

          let newPosition: number;
          let remainingLengthOfPhase: number;
          // WI #5622 — a fixed milestone in the drag direction bounds the
          // preview exactly like the backend rescale (compressInPhase.ts).
          const wall = this.compressWallPosition;
          if (this.posX > this.compressToolAnchor) {
            remainingLengthOfPhase =
              (wall ?? phase.end) - this.compressToolAnchor;
            newPosition = Math.round(
              (distanceFromStartDay / remainingLengthOfPhase) *
                (remainingLengthOfPhase - lengthToCompress) +
                lengthToCompress +
                this.compressToolAnchor,
            );
          } else if (this.posX < this.compressToolAnchor) {
            remainingLengthOfPhase =
              this.compressToolAnchor - (wall ?? phase.start + 1);
            newPosition = Math.round(
              (distanceFromStartDay / remainingLengthOfPhase) *
                (remainingLengthOfPhase - lengthToCompress) -
                lengthToCompress +
                this.compressToolAnchor,
            );
          } else {
            newPosition = activity.originalPosition;
          }

          return Math.round(newPosition);
        }

        return activity.originalPosition;
      };
    },

    activityLanes(): Record<string, { number: number; nextId?: string }> {
      const ioStore = useIoStore();
      const activityLanes: Record<string, { number: number; nextId?: string }> =
        {};

      ioStore.workstreams.forEach((ws) => {
        const idealMaxLanes = this.idealMaxLanesPerWorkstream[ws.id];
        // array of maxLanes filled with 0s
        const lanesPos: number[] = new Array(idealMaxLanes).fill(0);
        const workstreamLanes: string[] = [];

        ioStore.activitiesInWorkstream(ws.id).forEach((activity) => {
          // x position of activity
          const beginning = this.scaleX(this.activityPos(activity));

          const textSize = this.textSize('activity-label-' + activity.id);

          //width of activity
          const end = beginning + textSize + 50;

          // index of the lane where the activity should be placed
          let laneIndex = lanesPos.findIndex((laneEnd) => laneEnd < beginning);

          if (laneIndex === -1) {
            let distanceBetweenLabels = Infinity;
            workstreamLanes.forEach((id, i) => {
              const laneEnd = ioStore.activity(id).originalPosition;
              const dist = laneEnd - beginning;
              if (dist < distanceBetweenLabels) {
                distanceBetweenLabels = dist;
                laneIndex = i;
              }
            });
          }

          laneIndex = laneIndex >= 0 ? laneIndex : 0;
          activityLanes[activity.id] = { number: laneIndex };
          const prevId = workstreamLanes[laneIndex];
          if (prevId) {
            activityLanes[prevId].nextId = activity.id;
          }
          workstreamLanes[laneIndex] = activity.id;
          lanesPos[laneIndex] = end;
          this.workstreamLanes[activity.workstream] = workstreamLanes.length;
        });
      });

      return activityLanes;
    },

    activityLane() {
      return (activityId: string) => {
        if (!(activityId in this.activityLanes)) {
          throw new Error('missing activity in activity lanes');
        }

        return this.activityLanes[activityId];
      };
    },

    workstreamLanesAmount() {
      return (workstreamId: string) => {
        if (this.workstreamLanes[workstreamId] === undefined) {
          return 0;
        }

        return this.workstreamLanes[workstreamId];
      };
    },

    workstreamLaneHeight() {
      return (wsId: string) => {
        const lanesAmount = this.workstreamLanesAmount(wsId) + 1;
        const height = this.workstreamHeight(wsId);

        return height / lanesAmount;
      };
    },

    textSize() {
      return (activityId: string) => {
        const sizes = this.textSizes[activityId];
        if (!sizes || !sizes.length) {
          return 200;
        }

        return sizes[0];
      };
    },

    // returns the hovered workstream
    hoveredWorkstream(): MapWorkstream {
      const realY = START_Y > this.mouseY ? 0 : this.mouseY;
      const firstWorkstream = useIoStore().workstreamByPosition(0);
      // sort workstreams by position in reversed order
      // and find the first workstream that is hovered
      const workstream = orderBy(
        useIoStore().workstreams,
        ['position'],
        ['desc'],
      ).find((ws) => this.workstreamY(ws.id) < realY);

      return workstream || firstWorkstream;
    },

    hoveredIntermediateMilestone(): MapMilestone | undefined {
      return useIoStore().milestones.find(
        (m) => m.originalPosition === this.posX && !m.isFinal,
      );
    },

    hoveredFinalMilestone(): MapMilestone | undefined {
      return useIoStore().milestones.find(
        (m) => m.originalPosition === this.posX && m.isFinal,
      );
    },

    /** Cursor column sits on an existing milestone (MapGrabArea milestonePositions
     *  → blocks add-milestone placement there + shows the not-allowed cursor). */
    isOverExistingMilestone(): boolean {
      return useIoStore().milestones.some(
        (m) => m.originalPosition === this.posX,
      );
    },

    activityFinalColors(state) {
      return (activityId: string) => {
        return !state.isHidingActivityColors
          ? useIoStore().activityColors(activityId)
          : { color: undefined, label: undefined };
      };
    },

    hasLibraryActivityMix(): boolean {
      if (!this.isTemplate) {
        return false;
      }
      const activities = useIoStore().activities;
      let hasLibrary = false;
      let hasCustom = false;
      for (const activity of activities) {
        if (activity.parent) {
          hasLibrary = true;
        } else {
          hasCustom = true;
        }
        if (hasLibrary && hasCustom) {
          return true;
        }
      }

      return false;
    },

    focusedActivities(): MapActivity[] {
      const ioStore = useIoStore();
      if (
        !this.focusedFunctions.length &&
        !this.focusedUsers.length &&
        !this.focusedMirroredNames.length &&
        !this.focusedTags.length &&
        !this.focusedProjects.length &&
        !this.focusedModes.length &&
        !this.searchResults.length &&
        !this.isMeFilter &&
        !this.isFavoriteFilter &&
        !this.isRecentChangesFilter &&
        this.libraryFilter === 'none'
      ) {
        return [];
      }
      const username = useProfileStore().profile.username;
      const isTagFocused = (activity: MapActivity) =>
        activity.tags.some((id) => this.focusedTags.includes(id));
      const isModeFocused = (activity: MapActivity) =>
        this.focusedModes.includes(
          getItemMode(activity.state || 'unplanned', activity.status),
        );
      const isReferencedProjectFocused = (activity: MapActivity) =>
        this.focusedProjects.some((id) => activity.sourceProjectId === id);
      const role = this.focusedRole;
      const isFunctionFocused = (activity: MapActivity) =>
        this.focusedFunctions.some(
          (fnId) =>
            (role === 'responsible' &&
              (activity.responsible === fnId ||
                activity.criteriaResponsibles.includes(fnId) ||
                activity.actionsResponsibles.includes(fnId))) ||
            (role === 'approver' && activity.approverFunctions.includes(fnId)),
        );
      const isSearchFocused = (activity: MapActivity) =>
        this.searchResults.length > 0 &&
        this.searchResults.some((result) => result.id === activity.id);
      // A user matches when they are responsible at activity, action or
      // completion-criterion level. The per-level arrays hold usernames;
      // `responsibleUser` is a UserSummary — except on mirrored activities,
      // where the backend sends the source user's "first_name last_name"
      // DISPLAY NAME (see workflows' toMirroredActivitySummaryDto), so the
      // key set may contain display names too.
      const matchesUserKeys = (
        activity: MapActivity,
        keys: Set<string>,
        role: FilterRole,
      ) => {
        // The per-level arrays already hold effective usernames (see ioStore);
        // only the activity's own responsible still needs resolving.
        const effectiveResponsible = effectiveResponsibleUserOf(
          activity,
          activity.responsibleUser,
        );
        const responsibleKey =
          typeof effectiveResponsible === 'string'
            ? effectiveResponsible
            : effectiveResponsible?.username;

        return (
          (role === 'responsible' &&
            ((!!responsibleKey && keys.has(responsibleKey)) ||
              activity.criteriaResponsibleUsers.some((u) => keys.has(u)) ||
              activity.actionsResponsibleUsers.some((u) => keys.has(u)))) ||
          (role === 'approver' &&
            activity.approverUsers.some((u) => keys.has(u.username)))
        );
      };
      const meUsername = new Set([username]);
      const isUserFocused = (activity: MapActivity) =>
        this.isMeFilter &&
        this.meRoles.some((role) =>
          matchesUserKeys(activity, meUsername, role),
        );

      // WI #5605: the map sidebar's user list pushes user IDS into
      // `focusedUsers` (kanban's currency) and mirrored display names into
      // `focusedMirroredNames` (responsibles without a UserSummary in this
      // project). Resolve each focused id — via the user objects present on
      // the activities themselves — to BOTH the username (matches regular
      // activities) and the "first last" display name (matches that user's
      // mirrored activities, which only carry the display name).
      const focusedUserKeys = new Set<string>(this.focusedMirroredNames);
      if (this.focusedUsers.length) {
        const userById = new Map<string, UserSummary>();
        for (const activity of ioStore.activities) {
          // Completion users are focusable rows in the sidebar list, so they
          // must resolve here too or selecting one would match nothing.
          const responsibles = [
            activity.responsibleUser,
            activity.completionUser,
            ...activity.criteria.flatMap((criterion) => [
              criterion.responsibleUser,
              criterion.completionUser,
            ]),
            ...activity.actions.flatMap((action) => [
              action.responsibleUser,
              action.completionUser,
            ]),
            ...activity.approverUsers,
          ];
          for (const user of responsibles) {
            if (user && typeof user !== 'string') {
              userById.set(user.id, user);
            }
          }
        }
        for (const userId of this.focusedUsers) {
          const resolved = userById.get(userId);
          if (resolved) {
            focusedUserKeys.add(resolved.username);
            if (resolved.first_name && resolved.last_name) {
              focusedUserKeys.add(
                `${resolved.first_name} ${resolved.last_name}`,
              );
            }
          }
        }
      }
      const isFocusedUserMatch = (activity: MapActivity) =>
        focusedUserKeys.size > 0 &&
        matchesUserKeys(activity, focusedUserKeys, role);

      // WI #5680: "Assigned to me" is not a category of its own — it is OR'd
      // into the users category, so map and kanban share one rule.
      const isAnyUserFocused = (activity: MapActivity) =>
        isUserFocused(activity) || isFocusedUserMatch(activity);

      const isFavoriteFocused = (activity: MapActivity) => {
        if (!this.isFavoriteFilter) {
          return false;
        }

        return activity.isFavorite;
      };

      const isRecentChangesFocused = (activity: MapActivity) =>
        this.isRecentChangesFilter &&
        this.uniqueEntityIdsChanged.some((p) => p === activity.id);

      const isLibraryFocused = (activity: MapActivity) => {
        if (this.libraryFilter === 'library') {
          return !!activity.parent;
        }
        if (this.libraryFilter === 'custom') {
          return !activity.parent;
        }

        return false;
      };

      // WI #5680: categories combine with AND, values within a category with
      // OR. Only categories holding a selection constrain the result — every
      // `is*Focused` predicate returns false for its own empty category, so
      // AND-ing them all unconditionally would match nothing. Declaring the
      // categories as a list keeps a future one from silently dropping out.
      const activeCategories = [
        {
          isActive: this.focusedFunctions.length > 0,
          matches: isFunctionFocused,
        },
        {
          isActive:
            this.isMeFilter ||
            this.focusedUsers.length > 0 ||
            this.focusedMirroredNames.length > 0,
          matches: isAnyUserFocused,
        },
        { isActive: this.focusedTags.length > 0, matches: isTagFocused },
        {
          isActive: this.focusedProjects.length > 0,
          matches: isReferencedProjectFocused,
        },
        { isActive: this.focusedModes.length > 0, matches: isModeFocused },
        { isActive: this.searchResults.length > 0, matches: isSearchFocused },
        { isActive: this.isFavoriteFilter, matches: isFavoriteFocused },
        {
          isActive: this.isRecentChangesFilter,
          matches: isRecentChangesFocused,
        },
        { isActive: this.libraryFilter !== 'none', matches: isLibraryFocused },
      ].filter((category) => category.isActive);

      return ioStore.activities.filter((activity) =>
        activeCategories.every((category) => category.matches(activity)),
      );
    },

    focusedSelectableActivityIds(): string[] {
      const ioStore = useIoStore();

      return this.focusedActivities
        .filter(
          (activity) =>
            !activity.mirrored &&
            // Only activities that are actually on the map: matches sitting
            // in a hidden workstream or hidden phase are invisible and must
            // not end up in an (invisible) selection.
            !activity.isHiddenOnMap &&
            ioStore.phase(activity.phase).state !== 'done',
        )
        .map((activity) => activity.id);
    },

    focusedMilestones(): MapMilestone[] {
      const ioStore = useIoStore();
      if (
        !this.focusedFunctions.length &&
        !this.focusedUsers.length &&
        !this.focusedMirroredNames.length &&
        !this.focusedTags.length &&
        !this.focusedProjects.length &&
        !this.focusedModes.length &&
        !this.isSearching &&
        !this.isMeFilter
      ) {
        return [];
      }
      const isReferencedProjectFocused = (milestone: MapMilestone) =>
        this.focusedProjects.some((id) => milestone.sourceProjectId === id);

      return ioStore.milestones.filter((milestone) =>
        isReferencedProjectFocused(milestone),
      );
    },

    /**
     * WI #5596: a filter/search that dims non-matching activities is active.
     * Mirrors the guard in `isShadowedActivityId` (which deliberately omits
     * `focusedProjects` — a referenced-project focus surfaces through
     * `focusedActivities.length`).
     */
    hasActiveActivityFilter(): boolean {
      return (
        this.focusedActivities.length > 0 ||
        this.focusedFunctions.length > 0 ||
        this.focusedUsers.length > 0 ||
        this.focusedMirroredNames.length > 0 ||
        this.focusedTags.length > 0 ||
        this.focusedModes.length > 0 ||
        this.searchResults.length > 0 ||
        this.isMeFilter ||
        this.isFavoriteFilter ||
        this.isRecentChangesFilter ||
        this.libraryFilter !== 'none'
      );
    },

    /**
     * WI #5596: hide-mode has visible effect only while a filter/search is
     * active. Deliberately independent of the milestone overlay: hide-mode
     * acts on filter matching only, never on the overlay's shadow-everything
     * mechanism, so the overlay keeps rendering over the same hidden set.
     */
    isFilterHideActive(): boolean {
      return this.isFilterHideMode && this.hasActiveActivityFilter;
    },

    /**
     * WI #5596: workstreams with zero visible matching activities while
     * hide-mode is active. They collapse out of the layout exactly like
     * manually-hidden workstreams (geometry only) — they never join the
     * reveal-eye count or the placeholder strip, which stay manual-only.
     */
    filterHiddenWorkstreamIds(): Set<string> {
      const ids = new Set<string>();
      if (!this.isFilterHideActive) {
        return ids;
      }
      const ioStore = useIoStore();
      const focusedIds = new Set(this.focusedActivities.map(({ id }) => id));
      for (const ws of ioStore.workstreams) {
        // Manually hidden rows are already out of the layout (and counted).
        if (ws.isHiddenOnMap) {
          continue;
        }
        // Inlined `isHiddenMirroredActivity` / `isFilterHiddenActivityId`
        // (actions are not reachable from a getter's `this` type).
        const hasVisibleMatch = ioStore
          .activitiesInWorkstream(ws.id)
          .some(
            (a) =>
              !a.isHiddenOnMap &&
              !(this.isHideMirroredFilter && a.mirrored) &&
              focusedIds.has(a.id),
          );
        if (!hasVisibleMatch) {
          ids.add(ws.id);
        }
      }

      return ids;
    },

    /**
     * WI #5596: hide-mode is currently hiding at least one activity. Gates
     * the hidden-placeholder strip: filter-hidden activities park their link
     * anchors on the strip (getActivityY), so the row must be visible for
     * the links to point at something. The strip's count and reveal-eye stay
     * manual-only.
     */
    hasFilterHiddenActivities(): boolean {
      if (!this.isFilterHideActive) {
        return false;
      }
      const ioStore = useIoStore();
      const focusedIds = new Set(this.focusedActivities.map(({ id }) => id));

      return ioStore.activities.some(
        (a) =>
          !a.isHiddenOnMap &&
          !(this.isHideMirroredFilter && a.mirrored) &&
          !focusedIds.has(a.id),
      );
    },
  },

  actions: {
    createMapState(mapId: string, isTemplate: boolean): void {
      this.$state.mapId = mapId;
      this.$state.isTemplate = isTemplate;
      this.$state.zoom = isTemplate ? 10 : 2;
      this.$state.isHidingActivityColors = false;
      useUiStore().setLinkAndLabelModes();
      useUiStore().setPresentationMode();
    },

    toggleLinkMode() {
      if (this.linkMode === 'single') {
        this.linkMode = 'multi';
      } else if (this.linkMode === 'multi') {
        this.linkMode = 'all';
      } else if (this.linkMode === 'all') {
        this.linkMode = 'none';
      } else {
        this.linkMode = 'single';
      }
      useUiStore().saveUserConfig('linkMode', this.linkMode);
    },

    toggleLinkMilestoneMode() {
      if (this.linkMilestoneMode === 'required') {
        this.linkMilestoneMode = 'all';
      } else if (this.linkMilestoneMode === 'all') {
        this.linkMilestoneMode = 'none';
      } else {
        this.linkMilestoneMode = 'required';
      }
      useUiStore().saveUserConfig('linkMilestoneMode', this.linkMilestoneMode);
    },

    toggleLabelMode() {
      if (this.labelMode === 'auto') {
        this.labelMode = 'none';
      } else if (this.labelMode === 'title') {
        this.labelMode = 'auto';
      } else {
        this.labelMode = 'title';
      }
      useUiStore().saveUserConfig('labelMode', this.labelMode);
    },

    toggleActivityGrouping() {
      this.activityGroupingMode =
        this.activityGroupingMode === 'on' ? 'off' : 'on';
      useUiStore().saveUserConfig(
        'activityGroupingMode',
        this.activityGroupingMode,
      );
    },

    toggleTimeLineTemplateMode() {
      if (this.timeLineTemplateMode === 'none') {
        this.timeLineTemplateMode = 'positions';
      } else {
        this.timeLineTemplateMode = 'none';
      }
      useUiStore().saveUserConfig(
        'timeLineTemplateMode',
        this.timeLineTemplateMode,
      );
    },

    setReadonlyMode(value: boolean): void {
      this.$state.readonly = value;
    },

    setMapSize(width: number, height: number): void {
      this.$state.width = width;
      this.$state.height = height;
      this.setZoom(this.zoom);
    },

    setOffset(offset: number, overscroll = false): void {
      // A wheel zoom with a tool active may take the view past the map's
      // start or end (`overscroll`, zoomAtPointer); until it glides back,
      // other moves can bring it back but not take it further out.
      const minOffset = this.isOverscrolled
        ? Math.min(this.minOffset(), this.offset)
        : this.minOffset();
      const maxOffset = this.isOverscrolled
        ? Math.max(this.maxOffset(), this.offset)
        : this.maxOffset();

      let next = offset;
      if (!overscroll && offset < minOffset) {
        next = minOffset;
      } else if (!overscroll && offset > maxOffset) {
        next = maxOffset;
      }
      this.isOverscrolled = next < this.minOffset() || next > this.maxOffset();
      // The pointer keeps its place on screen while the map moves under it (a
      // wheel zoom or pan, the edge auto-scroll): its content position mouseX
      // moves with the map, so posX and what reads it — the space and compress
      // tools, a placement tool, the link line — stay at the pointer until it
      // moves again.
      this.mouseX += next - this.offset;
      this.offset = next;
    },

    moveOffset(amount = 25): void {
      this.setOffset(this.offset + amount);
    },

    setZoom(zoom: number, wheel: boolean = false) {
      let minZoom = this.minZoom;
      const maxZoom = MAX_ZOOM;

      // Calculate visible phase zoom adjustment
      const ioStore = useIoStore();
      if (ioStore.hiddenPhases.size > 0) {
        const visiblePhasesLength =
          ioStore.lastVisiblePhase.end - ioStore.firstVisiblePhase.start;
        const visiblePhaseZoom = this.maskSize.width / visiblePhasesLength;

        if (visiblePhaseZoom > minZoom) {
          minZoom = visiblePhaseZoom;
        }
      }

      // Handle zoom resistance for milestone overlay
      if (zoom < minZoom) {
        this.zoom = minZoom;

        // Increment resistance counter when trying to zoom out further
        if (wheel) {
          this.zoomResistanceCount = (this.zoomResistanceCount || 0) + 1;
        } else {
          this.zoomResistanceCount = 0;
        }

        // Activate overlay after 3-5 scroll attempts
        if (this.zoomResistanceCount >= 5 && !this.isTemplate) {
          this.isMilestoneOverlay = true;
        }
      } else if (zoom > maxZoom) {
        this.zoom = maxZoom;
        this.zoomResistanceCount = 0;
        this.isMilestoneOverlay = false;
      } else {
        this.zoom = zoom;
        this.zoomResistanceCount = 0;

        // Only disable overlay if zooming in from minZoom
        if (zoom > minZoom) {
          this.isMilestoneOverlay = false;
        }
      }
    },

    setViewLimits(initialPosition: number, finalPosition: number) {
      const diff =
        finalPosition < initialPosition ? 1 : finalPosition - initialPosition;
      const zoom = this.maskSize.width / diff;
      const finalZoom =
        zoom < this.minZoom ? this.minZoom : zoom > MAX_ZOOM ? MAX_ZOOM : zoom;
      this.setZoom(finalZoom);
      this.setOffset(initialPosition * finalZoom);
    },

    incrementZoom(mousePos: number, wheel: boolean = false) {
      this.zoomAtPointer(this.zoom + this.zoom / 8, mousePos, wheel);
    },

    decrementZoom(mousePos: number, wheel: boolean = false) {
      this.zoomAtPointer(this.zoom - this.zoom / 8, mousePos, wheel);
    },

    /**
     * Zoom with the date under the pointer staying there; mousePos is the
     * pointer, px right of the content start (left of it counts as the
     * start). Near the map's start or end, the ends hold the view and the
     * date slides toward them — but while a tool is active (it places, moves
     * or spaces what is under the pointer) the view reaches past them instead
     * (isOverscrolled). Over the space past an end the zoom holds that end
     * where it is.
     */
    zoomAtPointer(zoom: number, mousePos: number, wheel: boolean): void {
      const overscroll = this.toolMode !== 'normal';
      let x = Math.max(mousePos, 0);
      if (overscroll || this.isOverscrolled) {
        const startX = this.minOffset() - this.offset;
        const endX = this.maxOffset() + this.maskSize.width - this.offset;
        x = Math.min(Math.max(x, startX), endX);
      }
      const date = (this.offset + x) / this.zoom;
      this.setZoom(zoom, wheel);
      this.setOffset(date * this.zoom - x, overscroll);
    },

    selectLink(predecessorId: string, successorId: string) {
      this.selectedContext = 'link';
      this.selectedLink = [predecessorId, successorId];
    },

    setMouseX(pos: number) {
      this.mouseX = pos + this.offset - START_X;
    },

    setMouseY(pos: number) {
      this.mouseY = pos;
    },

    animateMap(zoom: number, offset: number, duration = 500) {
      if (this.isAnimating) {
        return;
      }
      this.isAnimating = true;
      this.animateStep({
        initZoom: this.zoom,
        initOffset: this.offset,
        initTime: Date.now(),
        duration,
        zoom,
        offset,
      });
    },

    animateStep({
      initZoom,
      initOffset,
      initTime,
      duration,
      zoom,
      offset,
    }: AnimOpts) {
      const diffTime = Date.now() - initTime;
      if (diffTime > duration) {
        this.setZoom(zoom);
        this.setOffset(offset);
        this.isAnimating = false;

        return;
      }
      const ratio = 1 - easeOutQuint(diffTime / duration);
      this.setZoom(zoom - (zoom - initZoom) * ratio);
      this.setOffset(offset - (offset - initOffset) * ratio);
      window.requestAnimationFrame(() => {
        this.animateStep({
          initZoom,
          initOffset,
          initTime,
          duration,
          zoom,
          offset,
        });
      });
      this.setZoomConfig(this.offset, this.zoom);
    },

    animateTo(start: number, end: number) {
      const distance = end - start;
      const selectedPosition = start + distance / 2;
      const prezoom =
        distance * MAX_ZOOM < this.maskSize.width * 0.8
          ? MAX_ZOOM
          : (this.maskSize.width * 0.8) / distance;
      const zoom = prezoom > MAX_ZOOM ? MAX_ZOOM : prezoom;
      const x = selectedPosition * zoom - this.maskSize.width / 2;
      this.animateMap(zoom, x, 700);
    },

    resetMapZoom() {
      this.animateMap(this.minZoom, 0, 700);
    },

    focusSelection() {
      if (!this.selectedId || !this.selectedContext) {
        return;
      }
      switch (this.selectedContext) {
        case 'milestone':
          this.focusMilestone(this.selectedId);
          break;
        case 'phase':
          this.focusPhase(this.selectedId);
          break;
        case 'activity':
          this.focusActivity(this.selectedId);
          break;
        case 'mirroredActivity':
          this.focusActivity(this.selectedId);
          break;
        case 'workstream':
          this.focusWorkstream(this.selectedId);
          break;
      }
    },

    focusMilestone(msId: string) {
      const position = useIoStore().milestone(msId).originalPosition;
      this.animateTo(position, position);
    },

    focusAroundMilestone(msId: string) {
      const position = useIoStore().milestone(msId).originalPosition;
      this.animateViewLimits(position - 14, position + 28);
    },

    focusPhase(phaseId: string) {
      const phase = useIoStore().phase(phaseId);
      this.animateTo(phase.start, phase.end);
    },

    focusActivity(activityId: string) {
      const ioStore = useIoStore();
      const activity = ioStore.activity(activityId);
      const predecessors = activity.predecessors.map((id) =>
        ioStore.activity(id),
      );
      const successors = activity.successors.map((id) => ioStore.activity(id));
      this.focusActivities(predecessors.concat(successors).concat(activity));
    },

    focusWorkstream(streamId: string) {
      const activities = useIoStore().activities.filter(
        (act) => act.workstream === streamId,
      );
      this.focusActivities(activities);
    },

    focusActivities(activities: MapActivity[]) {
      const positions = activities.map((act) => this.activityPos(act));
      const start = Math.min(...positions);
      const end = activities.length === 1 ? start : Math.max(...positions);
      this.animateTo(start, end);
    },

    focusActivitiesAndMilestones(
      activities: MapActivity[],
      milestones: MapMilestone[],
    ) {
      const activityPositions = activities.map((act) => this.activityPos(act));
      const milestonePositions = milestones.map((ms) => this.milestonePos(ms));
      const positions = [...activityPositions, ...milestonePositions];
      const start = Math.min(...positions);
      const end = positions.length === 1 ? start : Math.max(...positions);
      this.animateTo(start, end);
    },

    animateViewLimits(
      initialPosition: number,
      finalPosition: number,
      duration = 700,
    ) {
      const diff =
        finalPosition < initialPosition ? 1 : finalPosition - initialPosition;
      const zoomCalc = this.maskSize.width / diff;
      const finalZoom =
        zoomCalc < this.minZoom
          ? this.minZoom
          : zoomCalc > MAX_ZOOM
            ? MAX_ZOOM
            : zoomCalc;
      const offset = initialPosition * finalZoom;
      this.animateMap(finalZoom, offset, duration);
    },

    focusToday() {
      const project = useProjectStore().project(this.mapId);
      const todayPosition = daysBetweenDates(
        project.start_date.split('T')[0],
        new Date().toISOString().split('T')[0],
      );
      this.animateViewLimits(todayPosition - 14, todayPosition + 28);
    },

    freezePositions() {
      const ioStore = useIoStore();
      this.frozenActivities = ioStore.activities.map((activity) => ({
        id: activity.id,
        position: this.activityPos(activity),
      }));
      this.frozenMilestones = ioStore.milestones.map((ms) => ({
        id: ms.id,
        position: this.milestonePos(ms),
      }));
      this.frozenPhases = ioStore.phases.map((phase) => ({
        id: phase.id,
        start: this.phaseStart(phase),
        duration: this.phaseDuration(phase),
      }));
    },

    unfreezePositions() {
      this.frozenActivities = [];
      this.frozenMilestones = [];
      this.frozenPhases = [];
    },

    setIsDragging(value: boolean) {
      this.isDragging = value;
    },

    toggleActivityColors() {
      this.isHidingActivityColors = !this.isHidingActivityColors;
      this.isHidingMilestoneColors = !this.isHidingMilestoneColors;
    },

    async mapSelectItem(context?: MapContext, itemId?: string) {
      if (
        !context ||
        !itemId ||
        (context === this.selectedContext && itemId === this.selectedId)
      ) {
        this.selectedContext = '';
        this.selectedId = '';
        this.selectedIds = [];
        this.selectedTaskId = '';
        this.selectedTaskType = '';

        return;
      }

      this.selectedIds = [];
      this.selectedContext = '';
      this.selectedId = '';

      await nextTick(() => {
        this.selectedContext = context;
        this.selectedId = itemId;
      });
    },

    isSelectedItem(context: MapContext, itemId?: string) {
      if (!itemId) {
        return false;
      }

      return (
        this.selectedContext === context &&
        (this.selectedId === itemId || this.selectedIds.includes(itemId))
      );
    },

    toggleFocusedTag(id: string) {
      if (this.focusedTags.includes(id)) {
        this.focusedTags = this.focusedTags.filter((tagId) => tagId !== id);
      } else {
        this.focusedTags.push(id);
      }
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    toggleFocusedReferencedProjects(id: string) {
      if (this.focusedProjects.includes(id)) {
        this.focusedProjects = this.focusedProjects.filter(
          (projectId) => projectId !== id,
        );
      } else {
        this.focusedProjects.push(id);
      }
      const focusedMilestones = this.focusedMilestones;
      if (this.focusedActivities.length || focusedMilestones.length) {
        this.focusActivitiesAndMilestones(
          this.focusedActivities,
          focusedMilestones,
        );
      }
    },

    // WI #5630: callers pass the full next selection — a single checkbox
    // toggle and the In-Progress group swap both reduce to replace +
    // re-frame, so this is the filter's only mutation entry point.
    setFocusedModes(modes: ItemMode[]) {
      this.focusedModes = modes;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    toggleFocusedMe() {
      this.isMeFilter = !this.isMeFilter;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    setFocusedRole(role: FilterRole) {
      this.focusedRole = role;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    setMeRoles(roles: FilterRole[]) {
      this.isMeFilter = roles.length > 0;
      this.meRoles = roles.length ? roles : ['responsible'];
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    // Kanban matches the responsible role only: drop any other role so no
    // invisible role filter carries over from the map.
    resetRolesToResponsible() {
      this.focusedRole = 'responsible';
      if (!this.meRoles.includes('responsible')) {
        this.isMeFilter = false;
      }
      this.meRoles = ['responsible'];
    },

    toggleFocusedFavorite() {
      this.isFavoriteFilter = !this.isFavoriteFilter;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    toggleLibraryFilter(value: 'library' | 'custom') {
      this.libraryFilter = this.libraryFilter === value ? 'none' : value;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    toggleRecentChanges() {
      this.isRecentChangesFilter = !this.isRecentChangesFilter;
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    toggleHideMirrored() {
      this.isHideMirroredFilter = !this.isHideMirroredFilter;
      if (this.isHideMirroredFilter) {
        this.$state.focusedProjects = [];
      }
    },

    // WI #5596: flip shadow→hide for all active filters/search. Persisted per
    // map (hidden-workstreams pattern); a no-op while no filter is active.
    toggleFilterHideMode() {
      this.isFilterHideMode = !this.isFilterHideMode;
      localStorage.setItem(
        `filterHideMode-${this.mapId}`,
        JSON.stringify(this.isFilterHideMode),
      );
    },

    loadFilterHideMode() {
      this.isFilterHideMode =
        localStorage.getItem(`filterHideMode-${this.mapId}`) === 'true';
    },

    toggleFocusedFunction(fnId: string) {
      if (this.focusedFunctions.includes(fnId)) {
        this.focusedFunctions = this.focusedFunctions.filter(
          (id) => id !== fnId,
        );
      } else {
        this.focusedFunctions.push(fnId);
      }
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    // Used by the project kanban board's user filter (focusMap stays false —
    // there is no visible map to animate) and, since WI #5605, by the map
    // sidebar's user list, which passes focusMap to frame the matches like
    // toggleFocusedFunction does.
    toggleFocusedUser(userId: string, focusMap = false) {
      if (this.focusedUsers.includes(userId)) {
        this.focusedUsers = this.focusedUsers.filter((id) => id !== userId);
      } else {
        this.focusedUsers.push(userId);
      }
      if (focusMap && this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    // WI #5605: map sidebar only — focuses a mirrored-only responsible that
    // has no UserSummary in this project, so only the display name sent on
    // the mirrored activity is known.
    toggleFocusedMirroredName(displayName: string) {
      if (this.focusedMirroredNames.includes(displayName)) {
        this.focusedMirroredNames = this.focusedMirroredNames.filter(
          (name) => name !== displayName,
        );
      } else {
        this.focusedMirroredNames.push(displayName);
      }
      if (this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    // WI #5605: the map sidebar switches its list between functions and
    // users. Clear only the counterpart selection so no invisible filter
    // survives the switch — every other filter stays active.
    setSidebarUserMode(userMode: boolean) {
      if (userMode) {
        this.focusedFunctions = [];
      } else {
        this.focusedUsers = [];
        this.focusedMirroredNames = [];
      }
    },

    // WI #5605: responsibilities can be edited away live while a user filter
    // is active. The map sidebar prunes selections whose derived user row
    // disappeared so no invisible shadow-everything filter survives (see
    // MapFunctionsFilter). Reassign only on an actual change — the canvas
    // watches these arrays deeply and repaints on identity change.
    pruneFocusedUsers(validIds: Set<string>, validNames: Set<string>) {
      if (this.focusedUsers.some((id) => !validIds.has(id))) {
        this.focusedUsers = this.focusedUsers.filter((id) => validIds.has(id));
      }
      if (this.focusedMirroredNames.some((name) => !validNames.has(name))) {
        this.focusedMirroredNames = this.focusedMirroredNames.filter((name) =>
          validNames.has(name),
        );
      }
    },

    setIsSearching(enabled: boolean) {
      this.isSearching = enabled;
    },

    setSearchResults(results: SearchMatch[], shouldFocus: boolean = true) {
      this.searchResults = results;
      if (shouldFocus && this.focusedActivities.length) {
        this.focusActivities(this.focusedActivities);
      }
    },

    setSearchQuery(query: string) {
      this.searchQuery = query;
    },

    setShowSearchResults(show: boolean) {
      this.showSearchResults = show;
    },

    setHoveredSearchActivity(activityId: string) {
      this.hoveredSearchActivity = activityId;
    },

    clearHoveredSearchActivity() {
      this.hoveredSearchActivity = '';
    },

    async performSemanticSearch(
      mapId: string,
      query: string,
      isTemplate = false,
    ) {
      this.setIsSearching(true);
      try {
        const apiResponse = isTemplate
          ? await useTemplateStore().performSemanticSearch(mapId, query)
          : await useProjectStore().performSemanticSearch(mapId, query);

        const aiResults: SearchMatch[] = apiResponse.map((item) => ({
          id: item.id,
          title: item.title,
          category: item.category,
          isMirroredActivity: item.is_mirrored_activity,
          searchType: 'ai',
          relevanceScore: item.relevance_score,
          matches: item.highlights.map((h) => ({
            property: formatPropertyName(h.property),
            text: h.text,
            objectTitle: h.object_title,
          })),
          objectTitles: item.highlights
            .filter((h) => h.object_title)
            .map((h) => h.object_title!),
        }));

        const likeResults = this.searchResults.filter(
          (r) => r.searchType === 'like',
        );
        this.setSearchResults([...likeResults, ...aiResults], true);
        this.setSearchQuery(query);
        this.setShowSearchResults(true);

        return aiResults;
      } finally {
        this.setIsSearching(false);
      }
    },

    clearFocused() {
      this.$state.focusedFunctions = [];
      this.$state.focusedUsers = [];
      this.$state.focusedMirroredNames = [];
      this.$state.focusedTags = [];
      this.$state.focusedModes = [];
      this.$state.focusedRole = 'responsible';
      this.$state.meRoles = ['responsible'];
      this.$state.searchResults = [];
      this.$state.searchQuery = '';
      this.$state.showSearchResults = false;
      this.$state.focusedProjects = [];
      this.$state.isRecentChangesFilter = false;
      this.$state.isMeFilter = false;
      this.$state.isFavoriteFilter = false;
      this.$state.isHideMirroredFilter = false;
      this.$state.libraryFilter = 'none';
    },

    isShadowedActivityId(actId: string): boolean {
      if (this.isMilestoneOverlay) {
        return true;
      }
      if (
        !this.focusedActivities.length &&
        !this.focusedFunctions.length &&
        !this.focusedUsers.length &&
        !this.focusedMirroredNames.length &&
        !this.focusedTags.length &&
        !this.focusedModes.length &&
        !this.searchResults.length &&
        !this.isMeFilter &&
        !this.isFavoriteFilter &&
        !this.isRecentChangesFilter &&
        this.libraryFilter === 'none'
      ) {
        return false;
      }

      return !this.focusedActivities.some(({ id }) => id === actId);
    },

    /**
     * WI #5596: hide-mode removes an activity a filter/search would shadow.
     * Acts only on filter matching — the milestone overlay's
     * shadow-everything branch of `isShadowedActivityId` is deliberately NOT
     * mirrored here (it would blank the map), and milestones are never
     * filter-hidden. Purely visual: the activity stays in the relationship
     * graph and keeps constraining move-tool limits.
     */
    isFilterHiddenActivityId(actId: string): boolean {
      if (!this.isFilterHideActive) {
        return false;
      }

      return !this.focusedActivities.some(({ id }) => id === actId);
    },

    isShadowedMilestoneId(milestoneId: string): boolean {
      if (
        !this.focusedActivities.length &&
        !this.focusedFunctions.length &&
        !this.focusedUsers.length &&
        !this.focusedMirroredNames.length &&
        !this.focusedTags.length &&
        !this.focusedModes.length &&
        !this.searchResults.length &&
        !this.isMeFilter
      ) {
        return false;
      }

      return !this.focusedMilestones.some(({ id }) => id === milestoneId);
    },

    isHiddenMirroredActivity(activityId: string): boolean {
      if (!this.isHideMirroredFilter) {
        return false;
      }

      const activity = useIoStore().activity(activityId);

      return activity?.mirrored === true;
    },

    isHiddenMirroredMilestone(milestoneId: string): boolean {
      if (!this.isHideMirroredFilter) {
        return false;
      }

      const milestone = useIoStore().milestone(milestoneId);

      return milestone?.mirrored === true;
    },

    getActivityY(activityId: string): number {
      if (!this.mapId) {
        return 0;
      }

      // activity is being movedVertically
      if (
        this.isMovingItemVertically &&
        this.movingContext === 'activity' &&
        this.movingTarget === activityId
      ) {
        if (this.movingItemYPos > this.movingItemYMax) {
          return this.movingItemYMax;
        }
        if (this.movingItemYPos < this.movingItemYMin) {
          return this.movingItemYMin;
        }

        return this.movingItemYPos;
      }
      const ioStore = useIoStore();
      const activity = ioStore.activity(activityId);

      //  if activity is in a hidden workstream, please it on the fake workstream at the top of the map
      // WI #5563: effective set, so a temporary expand parks every other
      // workstream's activities on the fake strip too.
      // WI #5596: every filter-hidden activity parks there too (not only
      // those of fully collapsed workstreams) — its links then point at the
      // placeholder row like hidden-workstream links do, instead of at an
      // empty spot mid-band where the flow layout no longer draws anything.
      if (
        (ioStore.hiddenWorkstreamIdsOnMap.has(activity.workstream) ||
          this.isFilterHiddenActivityId(activityId)) &&
        !this.isExportingPDF
      ) {
        // Strip centre — the placeholder can span the whole map when no
        // workstream row remains visible (WI #5596).
        return START_Y + this.hiddenWorkstreamsHeight / 2;
      }

      return (
        this.workstreamY(activity.workstream) +
        this.workstreamLaneHeight(activity.workstream) *
          this.getActivityFinalLane(activity)
      );
    },

    getActivityFinalLane(activity: MapActivity): number {
      if (!this.mapId) {
        return 0;
      }

      const activityLane = this.activityLane(activity.id).number;
      const lanesAmount = this.workstreamLanesAmount(activity.workstream);
      const finalLane = lanes[lanesAmount - 1][activityLane % lanesAmount] + 1;

      return finalLane;
    },

    async setToolMode(mode: ToolMode) {
      // Preserve item selection when switching between selection and move tools
      const selectionTools: ToolMode[] = ['normal', 'movingItem'];
      const preserveSelection =
        selectionTools.includes(this.$state.toolMode) &&
        selectionTools.includes(mode);

      if (!preserveSelection) {
        await this.mapSelectItem();
      }
      this.$state.toolMode = mode;

      // WI #5622 — compress fixes live for one compress-tool activation only.
      // Every tool change starts from a clean slate — entering compress
      // included, so a direct `toolMode = 'normal'` write elsewhere (header
      // edit, item clicks, toolbar) can never carry stale fixes into the next
      // activation.
      this.compressFixedMilestoneIds = [];
      this.hoveredCompressFixMilestoneId = '';

      if (
        mode === 'spacePhase' ||
        mode === 'spaceFull' ||
        mode === 'compress' ||
        mode === 'movingItem'
      ) {
        this.spaceToolAnchor = 0;
        this.spaceToolFixedX = undefined;
        this.compressToolAnchor = 0;
        this.compressToolFixedX = undefined;
        this.movingTarget = '';
        this.movingActivityIds = [];
        this.leftBehindActivityIds = [];
      }
    },

    /**
     * WI #5622 — flip a milestone's fixed state for the current compress-tool
     * activation. Fixed milestones bound the compress drag and ride along in
     * the request as `fixed_milestone_ids`; nothing is persisted, and they
     * deliberately survive an individual apply (see setToolMode for the reset).
     */
    toggleCompressFixedMilestone(milestoneId: string): void {
      const index = this.compressFixedMilestoneIds.indexOf(milestoneId);
      if (index >= 0) {
        this.compressFixedMilestoneIds.splice(index, 1);
      } else {
        this.compressFixedMilestoneIds.push(milestoneId);
      }
    },

    clickBackground(): void {
      if (this.toolMode === 'addingMilestone') {
        this.isVisibleMilestoneModal = true;
      } else if (this.toolMode === 'addingActivity') {
        if (this.posX <= 0) {
          return;
        }

        // if mouse y is on fake workstream (if any hidden workstreams exist), do not open modal

        if (
          this.hiddenWorkstreamsHeight > 0 &&
          this.mouseY < START_Y + this.hiddenWorkstreamsHeight
        ) {
          return;
        }
        this.isVisibleActivityModal = true;
      } else if (this.isSplittingPhase) {
        if (this.posX <= 0) {
          return;
        }
        this.isVisiblePhaseModal = true;
      } else if (this.isLinking) {
        this.toolMode = 'normal';
      }
    },

    async clickActivity(event: PointerEvent, activityId: string) {
      const ioStore = useIoStore();
      if (event.metaKey || event.ctrlKey) {
        const selectedActivity = ioStore.activity(activityId);
        if (ioStore.phase(selectedActivity.phase).state === 'done') {
          return;
        }
        await this.selectActivities(activityId);

        return;
      }
      if (
        !this.isLinking &&
        !this.isMovingItem &&
        !this.isMovingItemVertically
      ) {
        const selectedActivity = ioStore.activity(activityId);
        if (!selectedActivity.mirrored) {
          await this.mapSelectItem('activity', activityId);
        } else {
          await this.mapSelectItem('mirroredActivity', activityId);
        }
      } else if (this.isLinking && this.selectedId) {
        if (this.selectedId === activityId) {
          return;
        }
        const selectedActivity = ioStore.activity(this.selectedId);
        const targetActivity = ioStore.activity(activityId);
        this.selectedLink =
          selectedActivity.originalPosition > targetActivity.originalPosition
            ? [activityId, this.selectedId]
            : [this.selectedId, activityId];
        this.isVisibleLinkModal = true;
      } else if (this.isMovingItem || this.isMovingItemVertically) {
        await this.mapSelectItem();
      } else {
        await this.mapSelectItem('activity', activityId);
      }
    },

    async selectActivities(activityId: string) {
      const ioStore = useIoStore();
      if (!this.selectedIds.length) {
        const { selectedContext, selectedId } = this;
        await this.mapSelectItem();
        if (selectedContext === 'activity' && selectedId) {
          const activity = ioStore.activity(selectedId);
          const isPhaseNotDone = ioStore.phase(activity.phase).state !== 'done';
          if (isPhaseNotDone) {
            await this.selectActivities(selectedId);
          }
        }
      }
      this.selectedContext = 'activity';
      if (this.selectedIds.includes(activityId)) {
        this.selectedIds = this.selectedIds.filter((id) => id !== activityId);
        if (!this.selectedIds.length) {
          await this.mapSelectItem();
        }
      } else {
        this.selectedIds.push(activityId);
      }
    },

    // Ctrl/Cmd+click on a collapsed group marker (canvas, WI #5531): toggle
    // the WHOLE group in the multi-selection, all-or-nothing — the same rule
    // as lasso/rect enclosure over the marker. Mirrored and done-phase
    // members are excluded, matching the enclosure eligibility filter.
    async selectActivityGroup(activityIds: string[]) {
      const ioStore = useIoStore();
      const eligible = activityIds.filter((id) => {
        const activity = ioStore.activity(id);

        return (
          !activity.mirrored && ioStore.phase(activity.phase).state !== 'done'
        );
      });
      if (!eligible.length) {
        return;
      }
      // Seed the multi-select from a current single selection, like
      // selectActivities (a mirrored selection has context
      // 'mirroredActivity' and is therefore never seeded).
      if (!this.selectedIds.length) {
        const { selectedContext, selectedId } = this;
        await this.mapSelectItem();
        if (selectedContext === 'activity' && selectedId) {
          const activity = ioStore.activity(selectedId);
          if (ioStore.phase(activity.phase).state !== 'done') {
            this.selectedIds = [selectedId];
          }
        }
      }
      this.selectedContext = 'activity';
      const allSelected = eligible.every((id) => this.selectedIds.includes(id));
      if (allSelected) {
        this.selectedIds = this.selectedIds.filter(
          (id) => !eligible.includes(id),
        );
        if (!this.selectedIds.length) {
          await this.mapSelectItem();
        }
      } else {
        const missing = eligible.filter((id) => !this.selectedIds.includes(id));
        this.selectedIds = [...this.selectedIds, ...missing];
      }
    },

    async selectAllFocusedActivities() {
      const eligible = this.focusedSelectableActivityIds;
      if (!eligible.length) {
        return;
      }
      if (eligible.length === 1) {
        await this.mapSelectItem('activity', eligible[0]);

        return;
      }
      await this.mapSelectItem();
      this.selectedContext = 'activity';
      this.selectedIds = eligible;
    },

    startRectangleSelect(x: number, y: number) {
      this.isRectangleSelecting = true;
      const clampedX = Math.max(START_X, Math.min(x, this.width));
      const clampedY = Math.max(START_Y, Math.min(y, this.height));
      this.rectangleSelectStart = { x: clampedX, y: clampedY };
      this.rectangleSelectEnd = { x: clampedX, y: clampedY };
    },

    updateRectangleSelect(x: number, y: number) {
      if (this.isRectangleSelecting) {
        const clampedX = Math.max(START_X, Math.min(x, this.width));
        const clampedY = Math.max(START_Y, Math.min(y, this.height));
        this.rectangleSelectEnd = { x: clampedX, y: clampedY };
      }
    },

    // `resolvePoint` lets the canvas renderer test enclosure against what it
    // actually draws — the layout engine's (x, y), with collapsed group
    // members substituted by their marker (WI #5531 grouping); the SVG path
    // omits it and the default projection + `getActivityY` apply.
    async finishRectangleSelect(
      addToSelection = false,
      resolvePoint?: (activityId: string) => { x: number; y: number },
    ) {
      if (
        !this.isRectangleSelecting ||
        !this.rectangleSelectStart ||
        !this.rectangleSelectEnd
      ) {
        this.cancelRectangleSelect();

        return;
      }

      const ioStore = useIoStore();
      const activitiesInRect = this.getActivitiesInRectangle(resolvePoint);

      // Filter out mirrored activities and activities in done phases
      const selectableActivities = activitiesInRect.filter((activityId) => {
        const activity = ioStore.activity(activityId);

        // Exclude mirrored activities
        if (activity.mirrored) {
          return false;
        }

        return ioStore.phase(activity.phase).state !== 'done';
      });

      if (selectableActivities.length > 0) {
        if (addToSelection && this.selectedIds.length > 0) {
          // Add to existing selection
          const newIds = selectableActivities.filter(
            (id) => !this.selectedIds.includes(id),
          );
          this.selectedIds = [...this.selectedIds, ...newIds];
        } else {
          // Replace selection
          await this.mapSelectItem();
          this.selectedContext = 'activity';
          this.selectedIds = selectableActivities;
        }
      } else if (!addToSelection) {
        // Clear selection if no activities found and not adding to selection
        await this.mapSelectItem();
      }

      this.cancelRectangleSelect();
    },

    cancelRectangleSelect() {
      this.isRectangleSelecting = false;
      this.rectangleSelectStart = null;
      this.rectangleSelectEnd = null;
    },

    getActivitiesInRectangle(
      resolvePoint?: (activityId: string) => { x: number; y: number },
    ): string[] {
      if (!this.rectangleSelectStart || !this.rectangleSelectEnd) {
        return [];
      }

      const ioStore = useIoStore();

      // Normalize rectangle coordinates (handle dragging in any direction)
      const minX = Math.min(
        this.rectangleSelectStart.x,
        this.rectangleSelectEnd.x,
      );
      const maxX = Math.max(
        this.rectangleSelectStart.x,
        this.rectangleSelectEnd.x,
      );
      const minY = Math.min(
        this.rectangleSelectStart.y,
        this.rectangleSelectEnd.y,
      );
      const maxY = Math.max(
        this.rectangleSelectStart.y,
        this.rectangleSelectEnd.y,
      );

      const activitiesInRect: string[] = [];

      for (const activity of ioStore.activities) {
        // Skip hidden activities (WI #5596: filter-hidden ones are invisible
        // and must not be rectangle-selectable either)
        if (
          activity.isHiddenOnMap ||
          this.isFilterHiddenActivityId(activity.id)
        ) {
          continue;
        }

        // Get activity screen position
        const { x: activityScreenX, y: activityScreenY } = resolvePoint
          ? resolvePoint(activity.id)
          : {
              x:
                this.scaleX(this.activityPos(activity)) + START_X - this.offset,
              y: this.getActivityY(activity.id),
            };

        // Check if activity center is within the selection rectangle
        if (
          activityScreenX >= minX &&
          activityScreenX <= maxX &&
          activityScreenY >= minY &&
          activityScreenY <= maxY
        ) {
          activitiesInRect.push(activity.id);
        }
      }

      return activitiesInRect;
    },

    startLassoSelect(x: number, y: number) {
      this.isLassoSelecting = true;
      const clampedX = Math.max(START_X, Math.min(x, this.width));
      const clampedY = Math.max(START_Y, Math.min(y, this.height));
      this.lassoSelectPoints = [{ x: clampedX, y: clampedY }];
    },

    updateLassoSelect(x: number, y: number) {
      if (this.isLassoSelecting) {
        const clampedX = Math.max(START_X, Math.min(x, this.width));
        const clampedY = Math.max(START_Y, Math.min(y, this.height));
        this.lassoSelectPoints.push({ x: clampedX, y: clampedY });
      }
    },

    // `resolvePoint` — see finishRectangleSelect.
    async finishLassoSelect(
      addToSelection = false,
      resolvePoint?: (activityId: string) => { x: number; y: number },
    ) {
      if (!this.isLassoSelecting || this.lassoSelectPoints.length < 3) {
        this.cancelLassoSelect();

        return;
      }

      const ioStore = useIoStore();
      const activitiesInLasso = this.getActivitiesInLasso(resolvePoint);

      // Filter out mirrored activities and activities in done phases
      const selectableActivities = activitiesInLasso.filter((activityId) => {
        const activity = ioStore.activity(activityId);

        // Exclude mirrored activities
        if (activity.mirrored) {
          return false;
        }

        return ioStore.phase(activity.phase).state !== 'done';
      });

      if (selectableActivities.length > 0) {
        if (addToSelection && this.selectedIds.length > 0) {
          // Add to existing selection
          const newIds = selectableActivities.filter(
            (id) => !this.selectedIds.includes(id),
          );
          this.selectedIds = [...this.selectedIds, ...newIds];
        } else {
          // Replace selection
          await this.mapSelectItem();
          this.selectedContext = 'activity';
          this.selectedIds = selectableActivities;
        }
      } else if (!addToSelection) {
        // Clear selection if no activities found and not adding to selection
        await this.mapSelectItem();
      }

      this.cancelLassoSelect();
    },

    cancelLassoSelect() {
      this.isLassoSelecting = false;
      this.lassoSelectPoints = [];
    },

    /**
     * Point-in-polygon algorithm using ray casting method
     * Returns true if point (x, y) is inside the polygon defined by points
     */
    isPointInPolygon(
      x: number,
      y: number,
      polygon: { x: number; y: number }[],
    ): boolean {
      let inside = false;
      const n = polygon.length;

      for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;

        if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
          inside = !inside;
        }
      }

      return inside;
    },

    getActivitiesInLasso(
      resolvePoint?: (activityId: string) => { x: number; y: number },
    ): string[] {
      if (this.lassoSelectPoints.length < 3) {
        return [];
      }

      const ioStore = useIoStore();
      const activitiesInLasso: string[] = [];

      for (const activity of ioStore.activities) {
        // Skip hidden activities (WI #5596: filter-hidden ones are invisible
        // and must not be lasso-selectable either)
        if (
          activity.isHiddenOnMap ||
          this.isFilterHiddenActivityId(activity.id)
        ) {
          continue;
        }

        // Get activity screen position
        const { x: activityScreenX, y: activityScreenY } = resolvePoint
          ? resolvePoint(activity.id)
          : {
              x:
                this.scaleX(this.activityPos(activity)) + START_X - this.offset,
              y: this.getActivityY(activity.id),
            };

        // Check if activity center is within the lasso polygon
        if (
          this.isPointInPolygon(
            activityScreenX,
            activityScreenY,
            this.lassoSelectPoints,
          )
        ) {
          activitiesInLasso.push(activity.id);
        }
      }

      return activitiesInLasso;
    },

    async clickMilestone(msId: string) {
      if (this.isLinking) {
        if (this.isTemplate) {
          const payload = {
            milestone: {
              id: msId,
              required: true,
            },
          };
          await useActivityTemplateStore()
            .update(this.mapId, this.selectedId, payload)
            .catch((error) => {
              const messages = useAlerts();
              messages.error(error);
            });
        } else {
          const payload = {
            milestone_relevance: {
              milestone: msId,
              required: true,
            },
          };
          await useActivityProjectStore()
            .update(this.mapId, this.selectedId, payload)
            .catch((error) => {
              const messages = useAlerts();
              messages.error(error);
            });
        }
      }
      this.toolMode = 'normal';
      const ioStore = useIoStore();
      const selectedMilestone = ioStore.milestone(msId);
      if (selectedMilestone.mirrored) {
        await this.mapSelectItem('mirroredMilestone', msId);
      } else {
        await this.mapSelectItem('milestone', msId);
      }
    },

    async clickPhase(phaseId: string) {
      if (this.isLinking) {
        return;
      }
      this.toolMode = 'normal';
      await this.mapSelectItem('phase', phaseId);
    },

    async clickWorkstream(wsId: string) {
      if (this.isLinking) {
        return;
      }
      this.toolMode = 'normal';
      await this.mapSelectItem('workstream', wsId);
    },

    getFinalPosition(activityId: string, position: number) {
      return this.movingTarget === activityId
        ? position - this.movingItemOffset
        : position;
    },

    setZoomConfig(offset: number, zoom: number): void {
      const mapId = this.mapId;
      const config = { offset, zoom };
      localStorage.setItem(`zoomConfig-${mapId}`, JSON.stringify(config));
    },

    getZoomConfig(): { offset: number; zoom: number } | null {
      const mapId = this.mapId;
      const config = localStorage.getItem(`zoomConfig-${mapId}`);
      if (!config) {
        this.isMilestoneOverlay = true;
      }

      return config ? JSON.parse(config) : null;
    },
  },
});

function easeOutQuint(x: number): number {
  return 1 - Math.pow(1 - x, 5);
}
